/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState, useMemo } from "react";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import MainNavigation from "@/components/layout/main-navigation";
import { Search } from "lucide-react";

interface Term {
  term: string;
  definition: string;
}

const GLOSSARY: Term[] = [
  {
    term: "Addendum",
    definition:
      "A document issued during bidding that changes or adds to the original plans or specs. Addenda can change scope, materials, or deadlines — always check for them before submitting a bid.",
  },
  {
    term: "Allowance",
    definition:
      "A placeholder dollar amount in an estimate for an item the client hasn't finalized yet. Common for fixtures, finishes, and appliances. The actual cost replaces the allowance once selections are made.",
  },
  {
    term: "As-Built",
    definition:
      "Drawings that show what was actually built, including any changes from the original plans. Required for close-out on most commercial jobs.",
  },
  {
    term: "Backfill",
    definition:
      "Filling an excavated area with soil, gravel, or other material after foundation or utility work is done. Material type and compaction requirements affect cost.",
  },
  {
    term: "Bid Bond",
    definition:
      "A guarantee from a surety company that you'll honor your bid price if selected. Usually 5-10% of the bid amount. Required on most public and many commercial projects.",
  },
  {
    term: "Blueprint",
    definition:
      "Construction drawings showing dimensions, materials, and details for a project. Historically printed in blue — now usually PDFs. Also called plans or drawings.",
  },
  {
    term: "BLS",
    definition:
      "Bureau of Labor Statistics. Federal agency that publishes wage data used to set labor rates. The OEWS (Occupational Employment and Wage Statistics) survey is the most common source for construction wage benchmarks.",
  },
  {
    term: "Burden Rate",
    definition:
      "The true cost of an employee beyond their hourly wage. Includes payroll taxes, workers comp, insurance, benefits, and PTO. A $30/hr worker might cost $42-48/hr fully burdened. If you're not billing the burdened rate, you're losing money on every hour.",
  },
  {
    term: "Change Order",
    definition:
      "A written agreement to modify the original scope, price, or schedule of a contract. Always document change orders in writing before doing the work — verbal agreements don't hold up.",
  },
  {
    term: "Closeout",
    definition:
      "The final phase of a project: punch list completion, final inspections, as-built delivery, warranty documentation, and final payment. Many contractors leave money on the table by dragging out closeout.",
  },
  {
    term: "Contingency",
    definition:
      "A percentage added to an estimate to cover unknowns. Typically 5-10% for straightforward work, 10-20% for remodels and renovation where you can't see behind walls. Not the same as profit — contingency covers risk.",
  },
  {
    term: "Cost Code",
    definition:
      "A numbering system to categorize costs by type (labor, material, equipment) and trade. Used for job costing and budget tracking. CSI MasterFormat is the most common system in commercial construction.",
  },
  {
    term: "Cost-Plus Contract",
    definition:
      "A contract where the client pays the actual cost of work plus an agreed markup or fee. Gives the contractor less risk but the client less price certainty. Common for remodels and custom homes.",
  },
  {
    term: "CSI Division",
    definition:
      "The Construction Specifications Institute organizes construction work into numbered divisions (e.g., Division 26 = Electrical, Division 22 = Plumbing). Used in commercial specs and cost coding.",
  },
  {
    term: "Direct Cost",
    definition:
      "Costs tied directly to a specific project — materials, labor, equipment, and subs. Does not include overhead or profit. Also called hard costs.",
  },
  {
    term: "Draw Schedule",
    definition:
      "A payment schedule tied to project milestones. The contractor invoices (draws) at each stage — foundation, framing, rough-in, etc. Protects both sides: contractor gets cash flow, owner pays for completed work.",
  },
  {
    term: "Drywall",
    definition:
      "Gypsum board (sheetrock) used for interior walls and ceilings. Comes in 4x8, 4x10, and 4x12 sheets. Standard is 1/2 inch — use 5/8 inch for fire rating or ceilings.",
  },
  {
    term: "Estimate",
    definition:
      "A detailed breakdown of what a project will cost — materials, labor, equipment, overhead, and profit. Not the same as a bid (which is the price you submit) or a quote (which is usually binding).",
  },
  {
    term: "Fixed-Price Contract",
    definition:
      "A contract where the price is set before work begins. The contractor takes on the risk of cost overruns but gets all the upside if the job comes in under budget. Also called lump-sum.",
  },
  {
    term: "Footings",
    definition:
      "The concrete base that supports a foundation or structure. Size depends on soil conditions, load, and local code. Usually the first concrete poured on a project.",
  },
  {
    term: "Framing",
    definition:
      "The structural skeleton of a building — studs, joists, rafters, headers. Wood framing uses 2x4s and 2x6s for walls, engineered lumber for longer spans. Steel framing is common in commercial.",
  },
  {
    term: "General Conditions",
    definition:
      "Indirect project costs that aren't tied to specific trade work — temporary power, dumpsters, portable toilets, site fencing, project management time. Usually 8-15% of the total project cost for a GC.",
  },
  {
    term: "General Contractor",
    definition:
      "The contractor responsible for the overall project. Coordinates subs, manages the schedule, handles inspections, and is the single point of contact for the owner. Also called the GC or prime contractor.",
  },
  {
    term: "Gross Margin",
    definition:
      "Revenue minus direct costs, divided by revenue. If you bill $100,000 and your direct costs are $75,000, your gross margin is 25%. This needs to cover your overhead — what's left after overhead is net profit.",
  },
  {
    term: "Hardscape",
    definition:
      "The non-living elements of a landscape — patios, retaining walls, walkways, driveways, fire pits. Materials include pavers, natural stone, concrete, and block. Usually priced per square foot installed.",
  },
  {
    term: "HVAC",
    definition:
      "Heating, Ventilation, and Air Conditioning. Includes furnaces, heat pumps, air conditioners, ductwork, thermostats, and controls. One of the highest-cost mechanical trades.",
  },
  {
    term: "Indirect Cost",
    definition:
      "Costs not tied to a specific project — office rent, insurance, accounting, marketing, vehicle payments. Also called overhead. Must be covered by your markup across all projects.",
  },
  {
    term: "Invoice",
    definition:
      "A bill sent to the client for work completed. Should reference the contract or proposal, list line items, and include payment terms. Progress invoices bill for work done to date; final invoices close out the project.",
  },
  {
    term: "Job Costing",
    definition:
      "Tracking actual costs against budget for each project. Compares what you estimated to what you actually spent on materials, labor, and equipment. The only way to know if you made money on a job.",
  },
  {
    term: "Jobsite",
    definition:
      "The physical location where construction work takes place. Jobsite conditions (access, terrain, utilities) directly affect cost and should be assessed before bidding.",
  },
  {
    term: "Labor Burden",
    definition:
      "See Burden Rate. The loaded cost of labor including all employer-paid expenses beyond the base wage.",
  },
  {
    term: "Lien",
    definition:
      "A legal claim against a property for unpaid work. Mechanics liens protect contractors, subs, and suppliers who aren't paid. Filing deadlines and requirements vary by state — know your local rules.",
  },
  {
    term: "Liquidated Damages",
    definition:
      "A pre-agreed dollar amount the contractor pays per day if the project finishes late. Common in commercial contracts. Usually $200-2,000+ per calendar day depending on project size.",
  },
  {
    term: "LMN Method",
    definition:
      "Labor, Material, Negligibles. A common estimating structure: price labor, price materials, and add a small percentage for misc items (screws, tape, adhesive, etc.) that are too small to itemize individually.",
  },
  {
    term: "Markup",
    definition:
      "The percentage added on top of cost to set the selling price. A 50% markup on a $10,000 cost = $15,000 price. Not the same as margin — 50% markup equals 33.3% margin.",
  },
  {
    term: "Material Takeoff",
    definition:
      "The process of measuring and counting materials needed from the plans. How many sheets of drywall, linear feet of pipe, yards of concrete, etc. The foundation of every estimate.",
  },
  {
    term: "Mobilization",
    definition:
      "The cost to set up at a jobsite — delivering equipment, building temporary facilities, establishing access. Often a separate line item on commercial bids. Can be 2-5% of the project cost.",
  },
  {
    term: "NEC",
    definition:
      "National Electrical Code. The standard for safe electrical installation in the US. Updated every 3 years. Local jurisdictions may adopt it with amendments. Electricians must follow the edition adopted locally.",
  },
  {
    term: "Net Margin",
    definition:
      "What's left after all costs — direct costs AND overhead. If gross margin is 25% and overhead is 18%, net margin is 7%. This is your actual profit. Many contractors think they're making 25% when they're really making 7%.",
  },
  {
    term: "Notice to Proceed",
    definition:
      "A formal document from the owner authorizing the contractor to start work. The project schedule typically begins on the NTP date. Don't start work without one on commercial projects.",
  },
  {
    term: "Overhead",
    definition:
      "Business expenses not tied to a specific job — rent, insurance, office staff, vehicles, software, phones. Must be spread across all projects through markup. Typical contractor overhead runs 12-22% of revenue.",
  },
  {
    term: "Production Rate",
    definition:
      "How much work a crew can complete per hour or day. A drywall crew might hang 50 sheets per day; a painter might cover 300 sq ft per hour. The most important number in labor estimating — and the hardest to get right.",
  },
  {
    term: "Progress Payment",
    definition:
      "Partial payment made during a project based on work completed. Tied to the draw schedule or percentage of completion. Keeps the contractor's cash flow positive on longer projects.",
  },
  {
    term: "Proposal",
    definition:
      "A formal document presenting your price, scope, terms, and timeline to a client. More detailed than a bid — includes what's included, what's excluded, payment terms, and validity period. Your first impression.",
  },
  {
    term: "Punchlist",
    definition:
      "A list of items that need to be fixed or completed before final payment. Generated during a walkthrough near the end of the project. Keep punch lists short by doing quality work the first time.",
  },
  {
    term: "Purchase Order",
    definition:
      "A written order to a supplier for materials or equipment. Specifies quantities, prices, and delivery dates. Gives you a paper trail if there are disputes about what was ordered or quoted.",
  },
  {
    term: "Retainage",
    definition:
      "A percentage (usually 5-10%) held back from each payment until the project is complete. Protects the owner against incomplete work. Release of retainage typically happens 30-60 days after substantial completion.",
  },
  {
    term: "RFI",
    definition:
      "Request for Information. A formal question to the architect or engineer when something in the plans is unclear, missing, or conflicts. RFIs create a paper trail — use them instead of guessing.",
  },
  {
    term: "RFP",
    definition:
      "Request for Proposal. An invitation from an owner or GC to submit a bid on a project. Includes plans, specs, bid due date, and requirements. Respond only to RFPs where you have a realistic shot at winning.",
  },
  {
    term: "Rough-In",
    definition:
      "Installing the mechanical systems (plumbing, electrical, HVAC) before walls are closed up. Rough-in happens after framing and before drywall. This is when mistakes are cheapest to fix.",
  },
  {
    term: "Scope of Work",
    definition:
      "A detailed description of what work is included in a contract or estimate. The most important part of any proposal. Unclear scope is the #1 cause of disputes, change orders, and lost money.",
  },
  {
    term: "Softscape",
    definition:
      "The living elements of a landscape — plants, trees, shrubs, grass, mulch. Usually priced by quantity (per plant, per sq ft of sod, per cu yd of mulch).",
  },
  {
    term: "Subcontractor",
    definition:
      "A contractor hired by the GC to perform a specific trade or portion of work. Subs carry their own insurance and licenses. The GC is responsible for coordinating and paying subs.",
  },
  {
    term: "Submittal",
    definition:
      "Product data, shop drawings, or samples sent to the architect for approval before installation. Required on commercial projects to verify the right products are being used.",
  },
  {
    term: "Substantial Completion",
    definition:
      "The point at which a project is usable for its intended purpose, even if punch list items remain. Triggers retainage release timelines and warranty periods.",
  },
  {
    term: "Takeoff",
    definition:
      "Short for material takeoff. Measuring quantities from plans or documents to determine what materials and labor are needed. The starting point of every estimate.",
  },
  {
    term: "T&M",
    definition:
      "Time and Materials. A billing method where the client pays for actual labor hours and materials used, plus a markup. Lower risk for the contractor but requires detailed time and material tracking.",
  },
  {
    term: "Turnkey",
    definition:
      "A project delivered complete and ready to use — the owner just turns the key and walks in. Turnkey contracts cover everything from design through final cleanup.",
  },
  {
    term: "Unit Price",
    definition:
      "A price per unit of work — per square foot, per linear foot, per each. Useful when exact quantities aren't known at bid time. The contract adjusts based on actual quantities installed.",
  },
  {
    term: "Value Engineering",
    definition:
      "Finding ways to reduce cost without reducing quality or function. Substitute materials, simplify details, or change methods. Best done during estimating, not after the job starts.",
  },
  {
    term: "Work Area",
    definition:
      "A defined zone within a project used to organize estimates and track costs. Could be a room, a floor, a building, or a phase. Helps break large estimates into manageable sections.",
  },
  {
    term: "Work Order",
    definition:
      "A document authorizing specific work to be done — usually for service, maintenance, or small repair jobs. Includes description, location, priority, and assigned crew.",
  },
  // -------------------------------------------------------------------------
  // Expansion batch — appended for SEO long-tail coverage. New terms may be
  // added here in any order; the render pass sorts alphabetically within each
  // letter group.
  // -------------------------------------------------------------------------
  {
    term: "Accrual Basis",
    definition:
      "An accounting method that records revenue when earned and expenses when incurred, regardless of when cash changes hands. Required for most larger contractors and most lenders; more accurate for job cost tracking than cash basis.",
  },
  {
    term: "Additional Insured",
    definition:
      "A party (often the GC or owner) added to a subcontractor's insurance policy so they're covered under the sub's liability in the event of a claim. Required on most commercial jobs — typically confirmed via a Certificate of Insurance (COI).",
  },
  {
    term: "Aggregate",
    definition:
      "Crushed stone, gravel, or sand used as a base or filler in concrete, asphalt, and drainage work. Sized by gradation (e.g., #57 stone) and priced per ton or per cubic yard.",
  },
  {
    term: "AIA Contract",
    definition:
      "A standardized contract form published by the American Institute of Architects (e.g., A101, A201). Widely used on commercial jobs. AIA forms define relationships between owner, architect, and contractor and set defaults for payment, change orders, and claims.",
  },
  {
    term: "Anchor Bolt",
    definition:
      "A heavy-duty bolt embedded in concrete to anchor structural members (like sill plates, columns, or post bases) to the foundation. Spec'd by diameter, embedment depth, and projection.",
  },
  {
    term: "Apparent Low Bidder",
    definition:
      "The bidder with the lowest submitted price at bid opening. 'Apparent' because the bid must still be evaluated for responsiveness, bonding, and compliance before award.",
  },
  {
    term: "Assembly",
    definition:
      "A reusable bundle of materials, labor, and equipment that represents a standard unit of work (e.g., '4\" concrete slab per SF' or 'interior wall per LF'). Assemblies speed up estimating and keep costs consistent across jobs.",
  },
  {
    term: "Bank Hours",
    definition:
      "The credit hours an employee banks for overtime work, to be used as paid time off later. Common in union construction; affects labor burden and cash flow planning.",
  },
  {
    term: "Bar Joist",
    definition:
      "An open-web steel joist used for floor and roof framing in commercial buildings. Priced per piece with depth, span, and load rating.",
  },
  {
    term: "Beam",
    definition:
      "A horizontal structural member that supports loads from above (floor joists, roof rafters, or walls) and transfers them to columns or walls. Can be steel, concrete, or engineered wood.",
  },
  {
    term: "Beneficial Occupancy",
    definition:
      "The point at which an owner begins using part of the work even though the project isn't substantially complete. Often triggers partial release of retainage and starts warranty periods for that portion.",
  },
  {
    term: "Bid Day",
    definition:
      "The day bids are due and opened. A high-stress day for contractors juggling last-minute sub numbers, addenda, and final-price revisions. Often involves 20+ phone calls in the final hour before the deadline.",
  },
  {
    term: "Bid Shopping",
    definition:
      "The practice of disclosing a subcontractor's bid to competitors to get a lower price. Considered unethical by most trade associations and explicitly prohibited in many contracts.",
  },
  {
    term: "Bond Rate",
    definition:
      "The percentage premium charged by a surety to issue a performance or payment bond, typically 0.5%–3% of contract value depending on the contractor's credit and track record.",
  },
  {
    term: "Buyout",
    definition:
      "The process of issuing subcontracts and purchase orders after a job is awarded. Buyout margin is the difference between your estimated costs and the actual contract values you lock in — positive buyout becomes job profit.",
  },
  {
    term: "CAD",
    definition:
      "Computer-Aided Design. Software used by architects and engineers to produce drawings. Common formats include DWG, DXF, and PDF-exported drawings. Contractors use CAD files for takeoffs and shop drawing reviews.",
  },
  {
    term: "Caliper",
    definition:
      'In landscaping, the diameter of a tree trunk measured 6 inches above ground (for trees under 4" caliper) or 12 inches above ground (for larger trees). Caliper is the primary pricing driver for nursery stock.',
  },
  {
    term: "Callback",
    definition:
      "A return trip to a completed job to fix a problem — usually a defect, warranty claim, or punch list item. Callbacks eat margin fast; tracking them per crew is a useful quality KPI.",
  },
  {
    term: "CAP Rate",
    definition:
      "Capitalization rate — used in commercial real estate to estimate building value from net operating income. Not a contractor KPI, but commonly referenced by developer clients when negotiating price.",
  },
  {
    term: "Cash Basis",
    definition:
      "An accounting method that records revenue when cash is received and expenses when paid. Simpler than accrual but distorts job profitability — most lenders and bond sureties require accrual.",
  },
  {
    term: "Certificate of Insurance (COI)",
    definition:
      "A document from an insurance carrier confirming a contractor or sub carries valid coverage. GCs and owners require COIs before allowing subs on-site. Must name the GC/owner as additional insured on most jobs.",
  },
  {
    term: "Certified Payroll",
    definition:
      "A federal or state-required payroll report showing employee wages, hours, and benefits on prevailing-wage jobs. Submitted weekly on most public works projects. Miss one week and payment can be withheld.",
  },
  {
    term: "CM at Risk",
    definition:
      "Construction Manager at Risk — a delivery method where the CM commits to a Guaranteed Maximum Price (GMP) and assumes responsibility for cost overruns. Common on larger commercial and institutional jobs.",
  },
  {
    term: "COGS",
    definition:
      "Cost of Goods Sold — the direct costs of delivering a job (materials, labor, equipment, subs). Revenue minus COGS equals gross profit. Critical for understanding true job margin.",
  },
  {
    term: "Committed Cost",
    definition:
      "Money a contractor has promised to spend — via signed subcontracts, purchase orders, or equipment rentals — but hasn't yet paid. Committed cost plus paid cost equals total cost to date for a job.",
  },
  {
    term: "Concrete Masonry Unit (CMU)",
    definition:
      'A standardized concrete block used for walls, foundations, and retaining walls. Common nominal sizes: 8"x8"x16" and 4"x8"x16". Priced per block or per SF of wall area.',
  },
  {
    term: "Conduit",
    definition:
      "A pipe or tube that protects and routes electrical wiring. Common types: EMT (thin-wall steel), rigid, PVC, flex. Priced per linear foot plus fittings, typically with labor variance for installation environment.",
  },
  {
    term: "Critical Path",
    definition:
      "The sequence of activities in a project schedule that determines the shortest possible completion time. Delays on the critical path delay the whole project; non-critical delays can often be absorbed.",
  },
  {
    term: "Davis-Bacon Act",
    definition:
      "A federal law requiring contractors on federally-funded projects over $2,000 to pay workers prevailing wages. Triggers certified payroll and specific wage determinations. Many states have parallel 'little Davis-Bacon' laws.",
  },
  {
    term: "Design-Build",
    definition:
      "A delivery method where one entity handles both design and construction. Single point of responsibility for the owner; faster delivery but less architect independence than traditional design-bid-build.",
  },
  {
    term: "Design-Bid-Build",
    definition:
      "The traditional delivery method where an architect designs the project, contractors bid on the completed drawings, and the low bidder builds. Slowest but clearest separation of responsibilities.",
  },
  {
    term: "Differing Site Condition",
    definition:
      "A physical condition at the job site that differs from what was shown in the contract documents. Usually grounds for a change order or claim — but only if properly documented and notice requirements are met.",
  },
  {
    term: "Digitizer",
    definition:
      "A device or software tool used to convert paper blueprints into digital measurements. Modern takeoff software replaces hardware digitizers with on-screen measurement from PDF drawings.",
  },
  {
    term: "Direct Labor",
    definition:
      "Labor hours spent directly building the project — framing, pouring, installing. Contrasted with indirect labor (supervision, cleanup, management). Direct labor is the main driver of labor variance and productivity tracking.",
  },
  {
    term: "Draw",
    definition:
      "A partial payment released against a construction loan or contract, typically monthly, as work progresses. Draw requests are substantiated with invoices, lien waivers, and completed-work schedules.",
  },
  {
    term: "EMR",
    definition:
      "Experience Modification Rate — a multiplier applied to workers compensation insurance premiums based on a contractor's claims history. Below 1.0 is better than industry average; above 1.0 costs you money and can disqualify you from some jobs.",
  },
  {
    term: "Earned Value",
    definition:
      "A project management metric that combines scope, schedule, and cost to measure progress. Earned value = % complete × budget. Compared against actual cost and planned value to spot variance.",
  },
  {
    term: "Engineer's Estimate",
    definition:
      "An independent cost estimate prepared by the owner or their engineer before bids are opened, used as a benchmark to evaluate received bids. Often kept confidential until after bid opening.",
  },
  {
    term: "Equipment Rate",
    definition:
      "The hourly, daily, weekly, or monthly cost of owning or renting equipment (excavators, skid steers, lifts, etc.). Owned equipment rates should include depreciation, fuel, maintenance, and insurance.",
  },
  {
    term: "Escalation Clause",
    definition:
      "A contract clause that adjusts pricing if material or labor costs rise beyond a threshold during the job. Common in long-duration projects where fixed pricing would be too risky for the contractor.",
  },
  {
    term: "Fabrication",
    definition:
      "The process of building structural components (steel, ductwork, cabinetry) off-site in a shop before delivery to the job. Fabrication is cheaper than on-site assembly but requires accurate shop drawings.",
  },
  {
    term: "Fast Track",
    definition:
      "A project delivery approach where construction starts before design is complete. Faster to finish but risks rework when later design decisions conflict with already-built work.",
  },
  {
    term: "Fixture",
    definition:
      "A permanently installed piece of equipment — plumbing fixtures (toilets, sinks), light fixtures, or permanent appliances. Spec'd in schedules on drawings and often the largest single cost category on buildout jobs.",
  },
  {
    term: "Float",
    definition:
      "The amount of time a non-critical-path activity can be delayed without affecting the project completion date. Total float is free time at the activity level; free float is free time without impacting successor activities.",
  },
  {
    term: "GMP",
    definition:
      "Guaranteed Maximum Price — a contract type where the contractor commits to a cap on project cost. Costs below the cap save the owner money (sometimes shared with the contractor); costs above the cap eat into the contractor's profit.",
  },
  {
    term: "Gross Profit",
    definition:
      "Revenue minus direct costs (COGS). Before subtracting overhead. A common KPI for judging job-level profitability separate from the overhead burden of running the business.",
  },
  {
    term: "Hard Cost",
    definition:
      "Physical construction costs — materials, labor, equipment, subcontracts. Contrasted with soft cost (design fees, permits, legal, financing). On most jobs, hard costs are 70–85% of total project cost.",
  },
  {
    term: "Headcount",
    definition:
      "The number of workers on a crew or job. Drives labor cost per day and is the main lever for accelerating or decelerating schedule. Too-high headcount on a job site causes congestion and lost productivity.",
  },
  {
    term: "Hold Harmless",
    definition:
      "A contract clause where one party agrees not to hold the other liable for certain claims. Common in subcontracts where the sub agrees to hold the GC harmless for sub-caused damages. Often paired with indemnification.",
  },
  {
    term: "Indemnification",
    definition:
      "A contract obligation to cover another party's losses, damages, or liabilities arising from specified events. Broad indemnification clauses are a major risk for contractors and should be reviewed carefully.",
  },
  {
    term: "Inspection",
    definition:
      "A formal review by a building inspector, engineer, or owner's rep to verify work meets code and spec. Failed inspections delay schedule and often require rework. Track inspection results per phase.",
  },
  {
    term: "Joint Check",
    definition:
      "A check made out jointly to a contractor and their supplier or sub, ensuring the downstream party gets paid. Used on jobs where the GC or owner wants to prevent liens from unpaid subs.",
  },
  {
    term: "Joist",
    definition:
      "A horizontal structural member spanning between beams or walls, supporting floors or ceilings. Common materials: dimensional lumber, engineered I-joists, steel bar joists. Sized by span, load, and spacing.",
  },
  {
    term: "Labor Hour",
    definition:
      "One hour of work performed by one worker. Standard unit for measuring labor productivity and cost. '100 labor hours at $45/hr burdened = $4,500' is the basic math behind every labor line on an estimate.",
  },
  {
    term: "Lead Time",
    definition:
      "The time between ordering a material or piece of equipment and its arrival on-site. Long-lead items (custom glass, generators, structural steel) need to be identified and ordered early to avoid schedule delays.",
  },
  {
    term: "LF",
    definition:
      "Linear Foot — a unit of measure for items priced per length (trim, fencing, pipe, wire). Written on estimates as LF. One linear foot equals 12 inches.",
  },
  {
    term: "Light Gauge Metal Framing",
    definition:
      "Cold-formed steel framing used in place of wood studs for walls, partitions, and joists. Common on commercial buildouts where fire resistance or moisture concerns rule out wood. Priced per linear foot of stud plus track and fasteners.",
  },
  {
    term: "Load Calculation",
    definition:
      "An engineering calculation to determine heating, cooling, or electrical loads for a building. Drives equipment sizing. Manual J is the residential standard for HVAC; Manual D is for ductwork.",
  },
  {
    term: "Long Lead Item",
    definition:
      "A material or piece of equipment with an unusually long lead time — often 8+ weeks for construction. Examples: custom windows, generators, rooftop HVAC units, structural steel. Ordered early during the buyout phase to avoid schedule impact.",
  },
  {
    term: "Lookahead Schedule",
    definition:
      "A short-term project schedule (typically 2–3 weeks) that shows detailed tasks, crew assignments, and deliveries. Used in weekly coordination meetings to spot conflicts and confirm sub readiness.",
  },
  {
    term: "MBF",
    definition:
      'Thousand Board Feet — a unit of measure for lumber, where one board foot is 1"x12"x12". Rough lumber, timbers, and bulk framing material are often priced per MBF rather than per board.',
  },
  {
    term: "Mechanics Lien",
    definition:
      "A legal claim filed against a property by an unpaid contractor, sub, or supplier. Can block the owner from selling or refinancing until the lien is resolved. Powerful tool for getting paid — but the filing deadlines and notice requirements are strict.",
  },
  {
    term: "Milestone",
    definition:
      "A significant point in a project schedule (dry-in, slab poured, substantial completion). Milestones are used for progress payments, schedule tracking, and client communication.",
  },
  {
    term: "Mock-Up",
    definition:
      "A small-scale physical sample built on-site or in a shop before full production starts. Common for facades, paving patterns, and custom finishes. Approved mock-ups become the quality standard for the rest of the job.",
  },
  {
    term: "Monolithic Pour",
    definition:
      "A concrete pour where the slab and footing are placed at the same time in one continuous pour. Faster than separate pours but requires more planning for reinforcement and formwork.",
  },
  {
    term: "Nameplate Rating",
    definition:
      "The manufacturer-stated performance rating of a piece of equipment (horsepower, BTU, amperage). Used for load calculations, permit applications, and warranty claims.",
  },
  {
    term: "Net 30",
    definition:
      "Payment terms where the invoice is due 30 days after the invoice date. Common on commercial jobs. Net 60 and Net 90 stretch contractor cash flow — factor the float into bid pricing.",
  },
  {
    term: "Notice to Owner",
    definition:
      "A preliminary lien notice that a subcontractor or supplier serves on the property owner early in a job, preserving their right to file a mechanics lien if they go unpaid. Rules vary by state but the deadline is often within 20–45 days of first work.",
  },
  {
    term: "OCIP",
    definition:
      "Owner-Controlled Insurance Program — a wrap insurance policy where the owner provides general liability and workers comp for all contractors on a large project. Simplifies claims but requires contractors to back out insurance costs from their bids.",
  },
  {
    term: "Over-the-Shoulder Review",
    definition:
      "A quality check where a supervisor or senior estimator reviews a bid or piece of work before it's submitted. Catches errors that the original author can't see anymore. Much cheaper than fixing mistakes in the field.",
  },
  {
    term: "Parametric Estimate",
    definition:
      "A rough-order-of-magnitude estimate based on high-level metrics like cost per square foot or cost per unit. Used in early project phases before detailed drawings exist. Typical accuracy: ±25%.",
  },
  {
    term: "Pay When Paid",
    definition:
      "A subcontract clause where the sub is only paid after the GC receives payment from the owner. Legal in some states, restricted or unenforceable in others. Worth reading every subcontract for this clause.",
  },
  {
    term: "Performance Bond",
    definition:
      "A surety bond guaranteeing that a contractor will perform per the contract. If the contractor defaults, the surety pays to complete the work. Required on most public works and many commercial jobs.",
  },
  {
    term: "Pitch",
    definition:
      "The slope of a roof, expressed as rise over run (e.g., 6:12 = 6 inches of rise per 12 inches of run). Affects labor productivity, waste factors, and whether fall protection is required.",
  },
  {
    term: "Plans",
    definition:
      "The set of drawings that define a construction project: floor plans, elevations, sections, details, and specifications. Plans are the legal document contractors bid against and build from.",
  },
  {
    term: "Pre-Bid Meeting",
    definition:
      "A meeting held before bids are due where the owner and architect walk contractors through the project and answer questions. Attendance is sometimes required to submit a bid.",
  },
  {
    term: "Pre-Task Plan",
    definition:
      "A daily safety document where a crew reviews the hazards of that day's work before starting. Common on commercial and industrial jobs. Required by OSHA in high-risk activities.",
  },
  {
    term: "Prevailing Wage",
    definition:
      "A government-set minimum wage for construction workers on public works projects. Varies by trade, locality, and classification. Higher than typical market wages in most areas.",
  },
  {
    term: "Productivity",
    definition:
      "A measure of how much work a crew accomplishes per unit of time (SF per hour, LF per day, units per shift). Productivity tracking drives labor variance factors and crew benchmarking.",
  },
  {
    term: "Progress Billing",
    definition:
      "An invoicing method where contractors bill for work completed to date rather than a lump sum at the end. Standard on longer jobs. Structured around a schedule of values approved by the owner.",
  },
  {
    term: "Quantity Takeoff",
    definition:
      "The process of counting, measuring, and listing all the materials and labor required to complete a project from drawings and specs. The foundation of every estimate.",
  },
  {
    term: "Rebar",
    definition:
      'Reinforcing steel bars embedded in concrete to add tensile strength. Sized by diameter in eighths of an inch (#4 = 4/8" = 1/2"). Priced per ton or per piece, with labor for cutting, bending, and placing.',
  },
  {
    term: "Release of Lien",
    definition:
      "A signed document where a contractor or sub waives their right to file a mechanics lien for work completed to date. Conditional releases are tied to receipt of a specific payment; unconditional releases are not.",
  },
  {
    term: "RFP Response",
    definition:
      "A contractor's submission in response to a Request for Proposal. Usually includes a technical narrative, cost proposal, schedule, and qualifications. More involved than a simple bid.",
  },
  {
    term: "Schedule of Values",
    definition:
      "A breakdown of the contract price into line items tied to major cost categories. Used as the basis for progress billing — each billing period, the contractor marks the percent complete on each line.",
  },
  {
    term: "Self-Perform",
    definition:
      "When a contractor performs work with their own crews rather than subbing it out. GCs typically self-perform their strongest trade and sub the rest. Self-perform work usually has higher margins but more risk.",
  },
  {
    term: "Shop Drawing",
    definition:
      "A detailed drawing produced by a fabricator or installer showing exactly how a component will be built and installed. Submitted to the architect for approval before fabrication. Common for steel, cabinets, and precast.",
  },
  {
    term: "Specs",
    definition:
      "The specifications section of contract documents — detailed written requirements for materials, methods, and quality standards. Organized by CSI division on commercial jobs. Where the plans say 'what and where,' the specs say 'how and to what standard.'",
  },
  {
    term: "Square",
    definition:
      "A unit of measure in roofing equal to 100 square feet of roof area. '32 squares of 30-year architectural shingle' is a common way to describe a residential roof.",
  },
  {
    term: "Starter Course",
    definition:
      "The first row of shingles, brick, or siding along an eave or bottom edge. Often a different material or orientation than the rest of the courses. Essential for proper water shedding.",
  },
  {
    term: "Subcontractor Agreement",
    definition:
      "The written contract between a GC and a sub. Defines scope, price, schedule, insurance requirements, payment terms, and change order process. Never let a sub on-site without one signed.",
  },
  {
    term: "Superintendent",
    definition:
      "The field-level leader on a construction site. Coordinates crews and subs, enforces safety and quality, and serves as the main point of contact for the owner's rep. Most important person on the job.",
  },
  {
    term: "Swing Stage",
    definition:
      "A suspended scaffold lowered from a roof or parapet for exterior work on tall buildings. Used for facade repair, window washing, and tuckpointing. Requires OSHA training and fall protection.",
  },
  {
    term: "Tuckpointing",
    definition:
      "The process of removing deteriorated mortar from masonry joints and replacing it with new mortar. Restores appearance and structural integrity of old brick and stone walls.",
  },
  {
    term: "Waste Factor",
    definition:
      "A multiplier applied to material quantities to account for cutting waste, damaged material, and over-ordering. Typical: 5% for framing lumber, 10% for drywall, 15% for tile, 20%+ for complex roofing.",
  },
  {
    term: "WIP",
    definition:
      "Work in Progress — an accounting concept tracking jobs that are started but not yet complete. WIP reports show revenue earned, costs incurred, and profit/loss per active job. Critical for contractors on percentage-of-completion accounting.",
  },
  {
    term: "Workers Compensation",
    definition:
      "Insurance that covers medical costs and lost wages for employees injured on the job. Premiums are based on payroll, trade class, and EMR. One of the three big labor burden costs alongside payroll taxes and health benefits.",
  },
  {
    term: "Zoning",
    definition:
      "The municipal regulations that dictate what can be built where — setbacks, heights, uses, lot coverage. Zoning review happens before building permit review and can kill a project before construction starts.",
  },
];

export function GlossaryPageClient() {
  const [search, setSearch] = useState("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  const letters = useMemo(() => {
    const set = new Set(
      GLOSSARY.map((t) => (t.term[0] ?? "").toUpperCase()).filter(Boolean),
    );
    return Array.from(set).sort();
  }, []);

  const filtered = useMemo(() => {
    let terms = GLOSSARY;
    if (search) {
      const q = search.toLowerCase();
      terms = terms.filter(
        (t) =>
          t.term.toLowerCase().includes(q) ||
          t.definition.toLowerCase().includes(q),
      );
    }
    if (activeLetter) {
      terms = terms.filter(
        (t) => (t.term[0] ?? "").toUpperCase() === activeLetter,
      );
    }
    return terms;
  }, [search, activeLetter]);

  const grouped = useMemo(() => {
    const map = new Map<string, Term[]>();
    for (const term of filtered) {
      const firstChar = term.term[0];
      if (!firstChar) continue;
      const letter = firstChar.toUpperCase();
      if (!map.has(letter)) map.set(letter, []);
      const arr = map.get(letter);
      if (arr) arr.push(term);
    }
    // Sort terms alphabetically within each letter group so the GLOSSARY
    // source array can be append-friendly without breaking user-facing order.
    for (const [, arr] of map) {
      arr.sort((a, b) => a.term.localeCompare(b.term));
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <MarketingPageHero
        variant="dark"
        badge="Glossary"
        title="Construction Terms in Plain English"
        description={`${GLOSSARY.length} terms defined the way contractors actually talk about them. No textbook definitions — just what you need to know.`}
      />

      <section className="py-12 md:py-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          {/* Search */}
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search terms..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveLetter(null);
              }}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            />
          </div>

          {/* Alphabet bar */}
          <div className="flex flex-wrap gap-1 mb-8">
            <button
              onClick={() => setActiveLetter(null)}
              className={`px-2.5 py-1 rounded text-sm font-medium transition-colors ${
                !activeLetter
                  ? "bg-secondary text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              All
            </button>
            {letters.map((letter) => (
              <button
                key={letter}
                onClick={() => {
                  setActiveLetter(activeLetter === letter ? null : letter);
                  setSearch("");
                }}
                className={`px-2.5 py-1 rounded text-sm font-medium transition-colors ${
                  activeLetter === letter
                    ? "bg-secondary text-white"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                {letter}
              </button>
            ))}
          </div>

          {/* Terms */}
          {grouped.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No terms match your search.
            </p>
          ) : (
            grouped.map(([letter, terms]) => (
              <div key={letter} className="mb-8" id={`letter-${letter}`}>
                <h2 className="text-2xl font-bold text-foreground mb-4 border-b border-border pb-2">
                  {letter}
                </h2>
                <div className="space-y-4">
                  {terms.map((t) => (
                    <div key={t.term}>
                      <h3 className="text-base font-semibold text-foreground">
                        {t.term}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                        {t.definition}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
